import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses";
const PRESIGNED_API =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/PresignedUrl/GeneratePresignedUrl";

async function uploadViaPresigned(file, folder = "courses/notes") {
  const res = await fetch(PRESIGNED_API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `${localStorage.getItem("SLNAdminToken") ?? ""}`,
    },
    body: JSON.stringify({ folder, fileNames: [file.name] }),
  });
  if (!res.ok) throw new Error(`Presigned URL error: HTTP ${res.status}`);
  const data = await res.json();
  const entry = data.urls?.[0];
  if (!entry?.signedUrl) throw new Error("No signedUrl returned from API.");
  const putRes = await fetch(entry.signedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!putRes.ok) throw new Error(`S3 upload failed: HTTP ${putRes.status}`);
  return entry.key;
}

const RemoveBtn = ({ onClick, disabled }) => (
  <button
    type="button"
    className="btn btn-sm btn-outline-danger rounded-circle p-0 ms-2"
    style={{ width: 28, height: 28, lineHeight: 1 }}
    onClick={onClick}
    disabled={disabled}
    title="Remove"
  >
    <i className="bi bi-x"></i>
  </button>
);

const emptyFile = (existingUrl = "") => ({
  file: null,
  s3Url: existingUrl,
  uploading: false,
  error: "",
});

const newIntroVideo = () => ({
  _key: crypto.randomUUID(),
  title: "",
  url: "",
  notes: [],
});

const newIntroNote = () => ({
  _key: crypto.randomUUID(),
  title: "",
  ...emptyFile(),
});

const newLessonVideo = () => ({
  _key: crypto.randomUUID(),
  title: "",
  url: "",
});

const newLesson = () => ({
  _key: crypto.randomUUID(),
  id: "",
  title: "",
  duration: "",
  videos: [newLessonVideo()],
  notes: [],
});

const newNote = () => ({
  _key: crypto.randomUUID(),
  title: "",
  ...emptyFile(),
});

// ── Module factory — prefilled title, imageFile is a proper upload object ─────
const newModule = (index = 0) => ({
  _key: crypto.randomUUID(),
  title: `Module ${index + 1}`, // ← prefilled, editable
  description: "",
  imageFile: emptyFile(), // ← replaces plain image string; stores { file, s3Url, uploading, error }
});

const newDemoVideo = () => ({
  _key: crypto.randomUUID(),
  url: "",
});

const lessonFromApi = (l) => ({
  _key: crypto.randomUUID(),
  id: l.id ?? "",
  title: l.title ?? "",
  duration: l.duration ?? "",
  videos: l.videoUrls?.length
    ? l.videoUrls.map((u) =>
        typeof u === "string"
          ? { _key: crypto.randomUUID(), title: "", url: u }
          : {
              _key: crypto.randomUUID(),
              title: u.title ?? "",
              url: u.url ?? "",
            },
      )
    : [newLessonVideo()],
  notes: (l.notes ?? []).map((n) => ({
    _key: crypto.randomUUID(),
    title: typeof n === "string" ? "" : (n.title ?? ""),
    file: null,
    s3Url: typeof n === "string" ? n : (n.url ?? n.s3Url ?? ""),
    uploading: false,
    error: "",
  })),
});

const introFromApi = (raw) => {
  if (!raw?.length) return [newIntroVideo()];
  return raw.map((item) => {
    if (typeof item === "string") {
      return { _key: crypto.randomUUID(), title: "", url: item, notes: [] };
    }
    return {
      _key: crypto.randomUUID(),
      title: item.title ?? "",
      url: item.url ?? item.videoUrl ?? "",
      notes: (item.notes ?? []).map((n) => ({
        _key: crypto.randomUUID(),
        title: typeof n === "string" ? "" : (n.title ?? ""),
        file: null,
        s3Url: typeof n === "string" ? n : (n.url ?? n.s3Url ?? ""),
        uploading: false,
        error: "",
      })),
    };
  });
};

// ── Reusable note file row ────────────────────────────────────────────────────
const NoteFileRow = ({ note, onFile, onTitleChange, onRemove }) => (
  <div className="mb-2 p-2 border rounded-3 bg-white">
    <div className="row g-2 align-items-center">
      <div className="col-lg-4 col-md-4 col-12">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Note Title"
          value={note.title}
          onChange={(e) => onTitleChange(e.target.value)}
        />
      </div>
      <div className="col-lg-5 col-md-5 col-10">
        <input
          type="file"
          className="form-control form-control-sm"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </div>
      <div className="col-auto">
        <RemoveBtn onClick={onRemove} />
      </div>
    </div>
    <div className="small mt-1 ps-1">
      {note.uploading && (
        <span className="text-primary d-flex align-items-center gap-1">
          <span className="spinner-border spinner-border-sm" role="status" />{" "}
          Uploading…
        </span>
      )}
      {!note.uploading && note.s3Url && (
        <a
          href={note.s3Url}
          target="_blank"
          rel="noreferrer"
          className="text-success text-decoration-none d-flex align-items-center gap-1"
          title={note.s3Url}
        >
          <i className="bi bi-check-circle-fill"></i>
          <span className="text-truncate" style={{ maxWidth: 300 }}>
            {note.file?.name ?? note.s3Url.split("/").pop()}
          </span>
        </a>
      )}
      {!note.uploading && note.error && (
        <span
          className="text-danger d-flex align-items-center gap-1"
          title={note.error}
        >
          <i className="bi bi-exclamation-circle-fill"></i> Upload failed —{" "}
          {note.error}
        </span>
      )}
      {!note.uploading && !note.s3Url && !note.error && (
        <span className="text-secondary">No file chosen</span>
      )}
    </div>
  </div>
);

// ── Module image upload row ───────────────────────────────────────────────────
const ModuleImageRow = ({ state, onFile }) => (
  <div className="d-flex align-items-center gap-2 p-2 border rounded-3 bg-white">
    <input
      type="file"
      accept="image/*"
      className="form-control form-control-sm w-75"
      onChange={(e) => onFile(e.target.files?.[0])}
    />
    <div className="flex-grow-1 small text-truncate">
      {state.uploading && (
        <span className="text-primary d-flex align-items-center gap-1">
          <span className="spinner-border spinner-border-sm" role="status" />{" "}
          Uploading…
        </span>
      )}
      {!state.uploading && state.s3Url && (
        <span className="text-success d-flex align-items-center gap-1">
          <i className="bi bi-check-circle-fill"></i>
          <span className="text-truncate" style={{ maxWidth: 180 }}>
            {state.file?.name ?? "Image uploaded"}
          </span>
        </span>
      )}
      {!state.uploading && state.error && (
        <span
          className="text-danger d-flex align-items-center gap-1"
          title={state.error}
        >
          <i className="bi bi-exclamation-circle-fill"></i> Upload failed
        </span>
      )}
      {!state.uploading && !state.s3Url && !state.error && (
        <span className="text-secondary">No image chosen</span>
      )}
    </div>
    {/* Preview thumbnail */}
    {state.s3Url && (
      <img
        src={state.s3Url}
        alt="module preview"
        style={{
          width: 52,
          height: 52,
          objectFit: "cover",
          borderRadius: 8,
          flexShrink: 0,
          border: "2px solid #dee2e6",
        }}
        onError={(e) => (e.target.style.display = "none")}
      />
    )}
  </div>
);

function AddCourse() {
  const navigate = useNavigate();
  const location = useLocation();

  const courseId = location.state?.courseId ?? null;
  const isEdit = Boolean(courseId);

  const [loadingCourse, setLoadingCourse] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    title: "",
    originalPrice: "",
    offeredPrice: "",
    category: "",
    description: "",
    about: "",
    objectives: "",
    details: "",
    certification: "",
    careerOpportunities: "",
    benefits: "",
    heroTitle: "",
    heroSubtitle: "",
    liveSessions: "",
    offerTitle: "",
    offerDescription: "",
    offerDeadline: "",
    status: "published",
    featured: false,
  });

  const [banner, setBanner] = useState(emptyFile());
  const [introVideos, setIntroVideos] = useState([newIntroVideo()]);
  const [instructor, setInstructor] = useState({
    id: "",
    name: "",
    description: "",
    profileImage: emptyFile(),
    rating: "",
  });
  const [lessons, setLessons] = useState([newLesson()]);
  const [courseModules, setCourseModules] = useState([newModule(0)]);
  const [demoVideos, setDemoVideos] = useState([newDemoVideo()]);

  // ── Prefill when editing ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    const fetchCourse = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/GetCourseById?courseId=${encodeURIComponent(courseId)}`,
          {
            headers: {
              Authorization: `${localStorage.getItem("SLNAdminToken") ?? ""}`,
            },
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const c = data.data ?? data.course ?? data;

        setForm({
          title: c.title ?? "",
          originalPrice: c.originalPrice ?? "",
          offeredPrice: c.offeredPrice ?? "",
          category: c.category ?? "",
          description: c.description ?? "",
          about: c.about ?? "",
          objectives: c.objectives ?? "",
          details: c.details ?? "",
          certification: c.certification ?? "",
          careerOpportunities: c.careerOpportunities ?? "",
          benefits: c.benefits ?? "",
          heroTitle: c.heroTitle ?? "",
          heroSubtitle: c.heroSubtitle ?? "",
          liveSessions: c.liveSessions ?? "",
          offerTitle: c.offerTitle ?? "",
          offerDescription: c.offerDescription ?? "",
          offerDeadline: c.offerDeadline ?? "",
          status: c.status ?? "published",
          featured: c.featured ?? false,
        });

        setBanner(emptyFile(c.banner ?? ""));
        setIntroVideos(introFromApi(c.introductionVideos));

        const ins = c.instructors?.[0];
        if (ins) {
          setInstructor({
            id: ins.id ?? "",
            name: ins.name ?? "",
            description: ins.description ?? "",
            profileImage: emptyFile(ins.profileImage ?? ""),
            rating: ins.rating ?? "",
          });
        }

        setLessons(
          c.lessons?.length ? c.lessons.map(lessonFromApi) : [newLesson()],
        );

        // ── prefill modules — existing image URL goes into imageFile.s3Url ──
        if (c.courseModules?.length) {
          setCourseModules(
            c.courseModules.map((m, i) => ({
              _key: crypto.randomUUID(),
              title: m.title ?? `Module ${i + 1}`,
              description: m.description ?? "",
              imageFile: emptyFile(m.image ?? ""),
            })),
          );
        }

        if (c.demoVideos?.length) {
          setDemoVideos(
            c.demoVideos.map((v) => ({
              _key: crypto.randomUUID(),
              url: typeof v === "string" ? v : (v.url ?? ""),
            })),
          );
        }
      } catch (err) {
        await Swal.fire({
          title: "Failed to load course",
          text: err.message,
          icon: "error",
          confirmButtonColor: "#dc3545",
          customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
        });
        navigate(-1);
      } finally {
        setLoadingCourse(false);
      }
    };
    fetchCourse();
  }, [courseId, isEdit, navigate]);

  const handleField = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleInstructorField = (field, val) =>
    setInstructor((p) => ({ ...p, [field]: val }));

  const handleFileUpload = async (file, setter, folder = "courses/banners") => {
    if (!file) return;
    setter({ file, s3Url: "", uploading: true, error: "" });
    try {
      const s3Url = await uploadViaPresigned(file, folder);
      setter({ file, s3Url, uploading: false, error: "" });
    } catch (err) {
      setter({ file, s3Url: "", uploading: false, error: err.message });
    }
  };

  // ── Intro video helpers ───────────────────────────────────────────────────
  const setIntroField = (key, field, val) =>
    setIntroVideos((p) =>
      p.map((v) => (v._key === key ? { ...v, [field]: val } : v)),
    );
  const addIntroVideo = () => setIntroVideos((p) => [...p, newIntroVideo()]);
  const removeIntroVideo = (key) =>
    setIntroVideos((p) => p.filter((v) => v._key !== key));
  const updateIntroNote = (vKey, nKey, patch) =>
    setIntroVideos((p) =>
      p.map((v) =>
        v._key === vKey
          ? {
              ...v,
              notes: v.notes.map((n) =>
                n._key === nKey ? { ...n, ...patch } : n,
              ),
            }
          : v,
      ),
    );
  const addIntroNote = (vKey) =>
    setIntroVideos((p) =>
      p.map((v) =>
        v._key === vKey ? { ...v, notes: [...v.notes, newIntroNote()] } : v,
      ),
    );
  const removeIntroNote = (vKey, nKey) =>
    setIntroVideos((p) =>
      p.map((v) =>
        v._key === vKey
          ? { ...v, notes: v.notes.filter((n) => n._key !== nKey) }
          : v,
      ),
    );
  const handleIntroNoteFile = async (vKey, nKey, file) => {
    if (!file) return;
    updateIntroNote(vKey, nKey, {
      file,
      s3Url: "",
      uploading: true,
      error: "",
    });
    try {
      const s3Url = await uploadViaPresigned(file, "courses/intro-notes");
      updateIntroNote(vKey, nKey, { s3Url, uploading: false });
    } catch (err) {
      updateIntroNote(vKey, nKey, { uploading: false, error: err.message });
    }
  };

  // ── Lesson helpers ────────────────────────────────────────────────────────
  const setLessonField = (key, field, val) =>
    setLessons((p) =>
      p.map((l) => (l._key === key ? { ...l, [field]: val } : l)),
    );
  const setLessonVideoField = (lKey, vKey, field, val) =>
    setLessons((p) =>
      p.map((l) =>
        l._key === lKey
          ? {
              ...l,
              videos: l.videos.map((v) =>
                v._key === vKey ? { ...v, [field]: val } : v,
              ),
            }
          : l,
      ),
    );
  const addLessonVideo = (lKey) =>
    setLessons((p) =>
      p.map((l) =>
        l._key === lKey ? { ...l, videos: [...l.videos, newLessonVideo()] } : l,
      ),
    );
  const removeLessonVideo = (lKey, vKey) =>
    setLessons((p) =>
      p.map((l) =>
        l._key === lKey
          ? { ...l, videos: l.videos.filter((v) => v._key !== vKey) }
          : l,
      ),
    );
  const addLesson = () => setLessons((p) => [...p, newLesson()]);
  const removeLesson = (key) =>
    setLessons((p) => p.filter((l) => l._key !== key));
  const updateNote = (lKey, nKey, patch) =>
    setLessons((p) =>
      p.map((l) =>
        l._key === lKey
          ? {
              ...l,
              notes: l.notes.map((n) =>
                n._key === nKey ? { ...n, ...patch } : n,
              ),
            }
          : l,
      ),
    );
  const addNote = (lKey) =>
    setLessons((p) =>
      p.map((l) =>
        l._key === lKey ? { ...l, notes: [...l.notes, newNote()] } : l,
      ),
    );
  const removeNote = (lKey, nKey) =>
    setLessons((p) =>
      p.map((l) =>
        l._key === lKey
          ? { ...l, notes: l.notes.filter((n) => n._key !== nKey) }
          : l,
      ),
    );
  const handleNoteFile = async (lKey, nKey, file) => {
    if (!file) return;
    updateNote(lKey, nKey, { file, s3Url: "", uploading: true, error: "" });
    try {
      const s3Url = await uploadViaPresigned(file, "courses/notes");
      updateNote(lKey, nKey, { s3Url, uploading: false });
    } catch (err) {
      updateNote(lKey, nKey, { uploading: false, error: err.message });
    }
  };

  // ── Module helpers ────────────────────────────────────────────────────────
  const setModuleField = (key, field, val) =>
    setCourseModules((p) =>
      p.map((m) => (m._key === key ? { ...m, [field]: val } : m)),
    );

  const handleModuleImageUpload = async (mKey, file) => {
    if (!file) return;
    setCourseModules((p) =>
      p.map((m) =>
        m._key === mKey
          ? { ...m, imageFile: { file, s3Url: "", uploading: true, error: "" } }
          : m,
      ),
    );
    try {
      const s3Url = await uploadViaPresigned(file, "courses/modules");
      setCourseModules((p) =>
        p.map((m) =>
          m._key === mKey
            ? { ...m, imageFile: { file, s3Url, uploading: false, error: "" } }
            : m,
        ),
      );
    } catch (err) {
      setCourseModules((p) =>
        p.map((m) =>
          m._key === mKey
            ? {
                ...m,
                imageFile: {
                  file,
                  s3Url: "",
                  uploading: false,
                  error: err.message,
                },
              }
            : m,
        ),
      );
    }
  };

  const addModule = () => setCourseModules((p) => [...p, newModule(p.length)]);
  const removeModule = (key) =>
    setCourseModules((p) => p.filter((m) => m._key !== key));

  // ── Demo video helpers ────────────────────────────────────────────────────
  const setDemoVideoField = (key, val) =>
    setDemoVideos((p) =>
      p.map((v) => (v._key === key ? { ...v, url: val } : v)),
    );
  const addDemoVideo = () => setDemoVideos((p) => [...p, newDemoVideo()]);
  const removeDemoVideo = (key) =>
    setDemoVideos((p) => p.filter((v) => v._key !== key));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!e.target.checkValidity()) {
      e.target.classList.add("was-validated");
      return;
    }

    const introUploading = introVideos.some((v) =>
      v.notes.some((n) => n.uploading),
    );
    const lessonUploading = lessons.some((l) =>
      l.notes.some((n) => n.uploading),
    );
    const moduleUploading = courseModules.some((m) => m.imageFile.uploading);
    const stillUploading =
      banner.uploading ||
      instructor.profileImage.uploading ||
      introUploading ||
      lessonUploading ||
      moduleUploading;

    const introError = introVideos.some((v) => v.notes.some((n) => n.error));
    const lessonError = lessons.some((l) => l.notes.some((n) => n.error));
    const moduleError = courseModules.some((m) => m.imageFile.error);
    const hasError =
      banner.error ||
      instructor.profileImage.error ||
      introError ||
      lessonError ||
      moduleError;

    if (stillUploading) {
      await Swal.fire({
        title: "Please wait",
        text: "Some files are still uploading.",
        icon: "info",
        confirmButtonColor: "#0d6efd",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
      return;
    }
    if (hasError) {
      await Swal.fire({
        title: "Upload Error",
        text: "One or more files failed to upload. Please re-upload them.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
      return;
    }

    const payload = {
      ...(isEdit && { courseId }),
      title: form.title,
      originalPrice: Number(form.originalPrice),
      offeredPrice: Number(form.offeredPrice),
      category: form.category,
      description: form.description,
      about: form.about,
      objectives: form.objectives,
      details: form.details,
      certification: form.certification,
      careerOpportunities: form.careerOpportunities,
      benefits: form.benefits,
      heroTitle: form.heroTitle,
      heroSubtitle: form.heroSubtitle,
      liveSessions: form.liveSessions ? Number(form.liveSessions) : undefined,
      offerTitle: form.offerTitle,
      offerDescription: form.offerDescription,
      offerDeadline: form.offerDeadline,
      status: form.status,
      featured: form.featured,
      // ── modules: send title, description, image (uploaded S3 URL) ──
      courseModules: courseModules.map(({ _key, imageFile, ...rest }) => ({
        ...rest,
        image: imageFile.s3Url ?? "",
      })),
      demoVideos: demoVideos.filter((v) => v.url.trim()).map((v) => v.url),
      banner: banner.s3Url,
      introductionVideos: introVideos
        .filter((v) => v.url.trim())
        .map(({ title, url, notes }) => ({
          title,
          url,
          notes: notes
            .filter((n) => n.s3Url)
            .map((n) => ({ title: n.title, url: n.s3Url })),
        })),
      instructors: [
        {
          id: instructor.id,
          name: instructor.name,
          description: instructor.description,
          profileImage: instructor.profileImage.s3Url,
          rating: Number(instructor.rating),
        },
      ],
      lessons: lessons.map(({ _key, notes, videos, ...rest }) => ({
        ...rest,
        videoUrls: videos
          .filter((v) => v.url.trim())
          .map(({ title, url }) => ({ title, url })),
        notes: notes
          .filter((n) => n.s3Url)
          .map((n) => ({ title: n.title, url: n.s3Url })),
      })),
    };

    setSubmitting(true);
    try {
      const res = await fetch(
        `${BASE_URL}/${isEdit ? "UpdateCourse" : "AddCourse"}`,
        {
          method: isEdit ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${localStorage.getItem("SLNAdminToken") ?? ""}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);
      await Swal.fire({
        title: isEdit ? "Course Updated" : "Course Published",
        text:
          data?.message ??
          (isEdit
            ? "Course updated successfully."
            : "Course added successfully."),
        icon: "success",
        confirmButtonColor: "#198754",
        confirmButtonText: "Go to Courses",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
      navigate("/manage/all-courses");
    } catch (err) {
      await Swal.fire({
        title: isEdit ? "Update Failed" : "Publish Failed",
        text: err.message || "Something went wrong.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reusable UI ───────────────────────────────────────────────────────────
  const SectionHead = ({ icon, title, badge }) => (
    <div className="col-12 mt-4 mb-1">
      <h6 className="fw-semibold text-dark d-flex align-items-center gap-2 mb-0">
        <i className={`bi ${icon} text-primary`}></i> {title}
        {badge && (
          <span className="badge bg-success-soft text-success fs-8 fw-normal rounded-pill">
            {badge}
          </span>
        )}
      </h6>
      <hr className="mt-1 mb-0" />
    </div>
  );

  const AddBtn = ({ onClick, label }) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-primary rounded-3 mt-1"
      onClick={onClick}
    >
      <i className="bi bi-plus-lg me-1"></i>
      {label}
    </button>
  );

  const FileUploadField = ({
    label,
    state,
    onFile,
    accept = "image/*",
    folder,
  }) => (
    <div>
      <label className="form-label small">{label}</label>
      <div className="d-flex align-items-center gap-2 p-2 border rounded-3 bg-white">
        <input
          type="file"
          accept={accept}
          className="form-control form-control-sm"
          style={{ maxWidth: 260 }}
          onChange={(e) =>
            handleFileUpload(e.target.files?.[0], onFile, folder)
          }
        />
        <div className="flex-grow-1 small text-truncate">
          {state.uploading && (
            <span className="text-primary d-flex align-items-center gap-1">
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              />{" "}
              Uploading…
            </span>
          )}
          {!state.uploading && state.s3Url && (
            <a
              href={state.s3Url}
              target="_blank"
              rel="noreferrer"
              className="text-success text-decoration-none d-flex align-items-center gap-1"
              title={state.s3Url}
            >
              <i className="bi bi-check-circle-fill"></i>
              <span className="text-truncate" style={{ maxWidth: 200 }}>
                {state.file?.name ?? "Uploaded"}
              </span>
            </a>
          )}
          {!state.uploading && state.error && (
            <span
              className="text-danger d-flex align-items-center gap-1"
              title={state.error}
            >
              <i className="bi bi-exclamation-circle-fill"></i> Upload failed
            </span>
          )}
          {!state.uploading && !state.s3Url && !state.error && (
            <span className="text-secondary">No file chosen</span>
          )}
        </div>
        {state.s3Url && (
          <img
            src={state.s3Url}
            alt="preview"
            style={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: 6,
              flexShrink: 0,
            }}
            onError={(e) => (e.target.style.display = "none")}
          />
        )}
      </div>
    </div>
  );

  if (loadingCourse) {
    return (
      <>
        <Header />
        <section>
          <div className="container py-5 text-center">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 text-secondary">Loading course data…</p>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Header />
      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pt-1 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div>
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    {isEdit ? "Edit Course" : "Add Course"}
                    {isEdit && (
                      <span className="badge bg-warning text-dark ms-2 fs-8 fw-normal align-middle">
                        {courseId}
                      </span>
                    )}
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    {isEdit
                      ? "Update the course details below"
                      : "Upload New course to our courses"}
                  </p>
                </div>
                <div>
                  <span
                    onClick={() => navigate(-1)}
                    className="btn btn-outline-secondary btn-sm px-3 rounded-pill"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-arrow-left me-1"></i> Back
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <form
              className="row g-2 needs-validation"
              noValidate
              onSubmit={handleSubmit}
            >
              {/* ── Basic Information ── */}
              <SectionHead icon="bi-info-circle" title="Basic Information" />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light">
                  <div className="row g-2">
                    <div className="col-lg-4 col-md-6 col-12">
                      <label className="form-label small">
                        Title <span className="text-danger fw-bold">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        className="form-control form-control-sm"
                        placeholder="e.g. Advanced React Mastery"
                        value={form.title}
                        onChange={handleField}
                        required
                      />
                      <div className="invalid-feedback">
                        Please enter a title.
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6 col-12">
                      <label className="form-label small">
                        Category <span className="text-danger fw-bold">*</span>
                      </label>
                      <input
                        type="text"
                        name="category"
                        className="form-control form-control-sm"
                        placeholder="e.g. Web Development"
                        value={form.category}
                        onChange={handleField}
                        required
                      />
                      <div className="invalid-feedback">
                        Please enter a category.
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-3 col-6">
                      <label className="form-label small">
                        Original Price (₹){" "}
                        <span className="text-danger fw-bold">*</span>
                      </label>
                      <input
                        type="number"
                        name="originalPrice"
                        className="form-control form-control-sm"
                        placeholder="4999"
                        min="0"
                        value={form.originalPrice}
                        onChange={handleField}
                        required
                      />
                      <div className="invalid-feedback">Required.</div>
                    </div>
                    <div className="col-lg-2 col-md-3 col-6">
                      <label className="form-label small">
                        Offered Price (₹){" "}
                        <span className="text-danger fw-bold">*</span>
                      </label>
                      <input
                        type="number"
                        name="offeredPrice"
                        className="form-control form-control-sm"
                        placeholder="1999"
                        min="0"
                        value={form.offeredPrice}
                        onChange={handleField}
                        required
                      />
                      <div className="invalid-feedback">Required.</div>
                    </div>
                    <div className="col-lg-6 col-12">
                      <FileUploadField
                        label="Banner Image"
                        state={banner}
                        onFile={setBanner}
                        accept="image/*"
                        folder="courses/banners"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Hero Section ── */}
              <SectionHead
                icon="bi-stars"
                title="Hero Banner Section"
                badge="New"
              />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light border-success">
                  <p className="text-secondary small mb-3">
                    <i className="bi bi-info-circle me-1"></i>
                    This powers the card below the video —{" "}
                    <em>"From Beginner to Professional Trader…"</em>
                  </p>
                  <div className="row g-2">
                    <div className="col-lg-6 col-12">
                      <label className="form-label small fw-semibold">
                        Hero Title{" "}
                        <span className="text-secondary fw-normal ms-1">
                          (Main heading on banner card)
                        </span>
                      </label>
                      <input
                        type="text"
                        name="heroTitle"
                        className="form-control form-control-sm"
                        placeholder="e.g. From Beginner to Professional Trader in Just 3 Months + Corporate Internship"
                        value={form.heroTitle}
                        onChange={handleField}
                      />
                      <div className="form-text">
                        Shown as the big h1 heading on the hero card.
                      </div>
                    </div>
                    <div className="col-lg-6 col-12">
                      <label className="form-label small fw-semibold">
                        Hero Subtitle{" "}
                        <span className="text-secondary fw-normal ms-1">
                          (Sub-line below the title)
                        </span>
                      </label>
                      <input
                        type="text"
                        name="heroSubtitle"
                        className="form-control form-control-sm"
                        placeholder="e.g. 3 Month me Stock Market ka Professional Course + Internship + Placement"
                        value={form.heroSubtitle}
                        onChange={handleField}
                      />
                      <div className="form-text">
                        Shown as the smaller paragraph below the heading.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Course Details ── */}
              <SectionHead icon="bi-text-paragraph" title="Course Details" />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light">
                  <div className="row g-2">
                    {[
                      {
                        name: "description",
                        label: "Description",
                        rows: 5,
                        required: true,
                      },
                      {
                        name: "about",
                        label: "About",
                        rows: 5,
                        required: false,
                      },
                      {
                        name: "objectives",
                        label: "Objectives",
                        rows: 3,
                        required: false,
                      },
                      {
                        name: "details",
                        label: "Details",
                        rows: 3,
                        required: false,
                      },
                    ].map(({ name, label, rows, required }) => (
                      <div className="col-lg-6 col-12" key={name}>
                        <label className="form-label small">
                          {label}{" "}
                          {required && (
                            <span className="text-danger fw-bold">*</span>
                          )}
                        </label>
                        <textarea
                          name={name}
                          className="form-control form-control-sm"
                          rows={rows}
                          placeholder={`Enter ${label.toLowerCase()}…`}
                          value={form[name]}
                          onChange={handleField}
                          required={required}
                        />
                        {required && (
                          <div className="invalid-feedback">
                            Please enter {label.toLowerCase()}.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Live Sessions + Modules ── */}
              <SectionHead
                icon="bi-collection-play"
                title="Live Sessions & Course Modules"
                badge="New"
              />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light border-success">
                  <p className="text-secondary small mb-3">
                    <i className="bi bi-info-circle me-1"></i>
                    Powers: <em>
                      "What Will You Learn Over X Live Sessions?"
                    </em>{" "}
                    section with module cards.
                  </p>

                  {/* Live sessions count */}
                  <div className="row g-2 mb-4">
                    <div className="col-lg-3 col-md-4 col-6">
                      <label className="form-label small fw-semibold">
                        No. of Live Sessions
                      </label>
                      <input
                        type="number"
                        name="liveSessions"
                        className="form-control form-control-sm"
                        placeholder="e.g. 20"
                        min="1"
                        value={form.liveSessions}
                        onChange={handleField}
                      />
                      <div className="form-text">
                        Shown as "X Live Sessions" in section heading.
                      </div>
                    </div>
                  </div>

                  <label className="form-label small fw-semibold mb-2 d-block">
                    Course Modules
                  </label>

                  {courseModules.map((mod, mIdx) => (
                    <div
                      key={mod._key}
                      className="border rounded-4 p-3 bg-white mb-3"
                    >
                      {/* Module header row */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="badge bg-primary-soft text-primary rounded-pill px-3 py-1">
                          <i className="bi bi-layers me-1"></i> Module{" "}
                          {mIdx + 1}
                        </span>
                        <RemoveBtn
                          onClick={() => removeModule(mod._key)}
                          disabled={courseModules.length === 1}
                        />
                      </div>

                      <div className="row g-3">
                        {/* ── Module Title — PREFILLED, editable ── */}
                        <div className="col-lg-4 col-md-5 col-12">
                          <label className="form-label small fw-semibold">
                            Module Title
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={`Module ${mIdx + 1}`}
                            value={mod.title}
                            onChange={(e) =>
                              setModuleField(mod._key, "title", e.target.value)
                            }
                          />
                          <div className="form-text">
                            Pre-filled as "Module {mIdx + 1}" — rename as
                            needed.
                          </div>
                        </div>

                        {/* ── Description / Bullet points ── */}
                        <div className="col-lg-8 col-md-7 col-12">
                          <label className="form-label small fw-semibold">
                            Points / Description
                            <span className="text-secondary fw-normal ms-1">
                              (one per line)
                            </span>
                          </label>
                          <textarea
                            className="form-control form-control-sm"
                            rows={4}
                            placeholder={
                              "Making Your Foundation Strong\nWhy do we need Technical Analysis\nCandlestick Patterns\nDemand & Supply theory"
                            }
                            value={mod.description}
                            onChange={(e) =>
                              setModuleField(
                                mod._key,
                                "description",
                                e.target.value,
                              )
                            }
                          />
                          <div className="form-text">
                            Each line becomes one bullet point on the course
                            page.
                          </div>
                        </div>

                        {/* ── Module Image Upload ── */}
                        <div className="col-8">
                          <label className="form-label small fw-semibold d-flex align-items-center gap-2 mb-1">
                            <i className="bi bi-image text-primary fs-6"></i>
                            Module Image
                            <span
                              className="badge bg-secondary-soft text-secondary fw-normal rounded-pill"
                              style={{ fontSize: 10 }}
                            >
                              optional
                            </span>
                          </label>
                          <ModuleImageRow
                            state={mod.imageFile}
                            onFile={(file) =>
                              handleModuleImageUpload(mod._key, file)
                            }
                          />
                          <div className="form-text mt-1">
                            Upload a JPG/PNG image for this module (recommended
                            800×500 px). Shown alongside module content on the
                            course page.
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <AddBtn onClick={addModule} label="Add Module" />
                </div>
              </div>

              {/* ── Benefits ── */}
              <SectionHead icon="bi-award" title="Benefits" />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light">
                  <div className="row g-2">
                    <div className="col-lg-4 col-12">
                      <label className="form-label small d-flex align-items-center gap-1">
                        <i className="bi bi-patch-check-fill text-warning"></i>{" "}
                        Certification
                      </label>
                      <textarea
                        name="certification"
                        className="form-control form-control-sm"
                        rows={7}
                        placeholder="e.g. Industry-recognised certificate upon completion…"
                        value={form.certification}
                        onChange={handleField}
                      />
                    </div>
                    <div className="col-lg-4 col-12">
                      <label className="form-label small d-flex align-items-center gap-1">
                        <i className="bi bi-briefcase-fill text-primary"></i>{" "}
                        Career Opportunities
                      </label>
                      <textarea
                        name="careerOpportunities"
                        className="form-control form-control-sm"
                        rows={7}
                        placeholder="e.g. Job-ready skills for frontend roles…"
                        value={form.careerOpportunities}
                        onChange={handleField}
                      />
                    </div>
                    <div className="col-lg-4 col-12">
                      <label className="form-label small d-flex align-items-center gap-1">
                        <i className="bi bi-gift-fill text-success"></i>{" "}
                        Benefits
                      </label>
                      <textarea
                        name="benefits"
                        className="form-control form-control-sm"
                        rows={7}
                        placeholder="e.g. Source code, lifetime access, real-world projects…"
                        value={form.benefits}
                        onChange={handleField}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Offer Section ── */}
              <SectionHead
                icon="bi-tag-fill"
                title="Urgency / Offer Banner"
                badge="New"
              />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light border-success">
                  <p className="text-secondary small mb-3">
                    <i className="bi bi-info-circle me-1"></i>
                    Powers the green <em>
                      "Register Before Midnight Of…"
                    </em>{" "}
                    section.
                  </p>
                  <div className="row g-2">
                    <div className="col-lg-5 col-12">
                      <label className="form-label small fw-semibold">
                        Offer Title
                      </label>
                      <input
                        type="text"
                        name="offerTitle"
                        className="form-control form-control-sm"
                        placeholder="e.g. To Unlock All Bonuses Worth ₹50,000"
                        value={form.offerTitle}
                        onChange={handleField}
                      />
                      <div className="form-text">
                        Shown as the small subtitle above the deadline.
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-4 col-12">
                      <label className="form-label small fw-semibold">
                        Deadline Text
                      </label>
                      <input
                        type="text"
                        name="offerDeadline"
                        className="form-control form-control-sm"
                        placeholder="e.g. 08 November 2025"
                        value={form.offerDeadline}
                        onChange={handleField}
                      />
                      <div className="form-text">
                        Shown bold inside{" "}
                        <em>"Register Before Midnight Of…"</em>
                      </div>
                    </div>
                    <div className="col-lg-4 col-12">
                      <label className="form-label small fw-semibold">
                        Offer Description
                      </label>
                      <textarea
                        name="offerDescription"
                        className="form-control form-control-sm"
                        rows={2}
                        placeholder="e.g. Register now and get exclusive bonuses worth ₹50,000 absolutely free."
                        value={form.offerDescription}
                        onChange={handleField}
                      />
                      <div className="form-text">
                        Optional body text below the deadline.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Demo Videos ── */}
              <SectionHead
                icon="bi-camera-video"
                title="Live Demo / Videos"
                badge="New"
              />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light border-success">
                  <p className="text-secondary small mb-3">
                    <i className="bi bi-info-circle me-1"></i>
                    Powers the <em>"Live Demo / Video"</em> section. Paste
                    YouTube embed URLs — the page renders them as iframes
                    automatically.
                  </p>
                  {demoVideos.map((vid, vIdx) => (
                    <div
                      key={vid._key}
                      className="row g-2 align-items-center mb-2"
                    >
                      <div className="col-auto">
                        <span
                          className="badge bg-secondary-soft text-secondary rounded-pill"
                          style={{ minWidth: 28 }}
                        >
                          {vIdx + 1}
                        </span>
                      </div>
                      <div className="col">
                        <input
                          type="url"
                          className="form-control form-control-sm"
                          placeholder="https://www.youtube.com/embed/VIDEO_ID?si=..."
                          value={vid.url}
                          onChange={(e) =>
                            setDemoVideoField(vid._key, e.target.value)
                          }
                        />
                      </div>
                      <div className="col-auto">
                        <RemoveBtn
                          onClick={() => removeDemoVideo(vid._key)}
                          disabled={demoVideos.length === 1}
                        />
                      </div>
                    </div>
                  ))}
                  <AddBtn onClick={addDemoVideo} label="Add Demo Video" />
                  <div className="form-text mt-2">
                    Add as many videos as you want. First & last show smaller,
                    the middle one shows larger — same layout as the course
                    page.
                  </div>
                </div>
              </div>

              {/* ── Introduction Videos ── */}
              <SectionHead icon="bi-play-btn" title="Introduction Videos" />
              {introVideos.map((video, vIdx) => (
                <div key={video._key} className="col-12">
                  <div className="border rounded-4 p-4 bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold text-secondary small">
                        Introduction Video {vIdx + 1}
                      </span>
                      <RemoveBtn
                        onClick={() => removeIntroVideo(video._key)}
                        disabled={introVideos.length === 1}
                      />
                    </div>
                    <div className="row g-2">
                      <div className="col-lg-4 col-md-5 col-12">
                        <label className="form-label small">Video Title</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="e.g. Course Overview"
                          value={video.title}
                          onChange={(e) =>
                            setIntroField(video._key, "title", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-lg-6 col-md-7 col-12">
                        <label className="form-label small">Video URL</label>
                        <input
                          type="url"
                          className="form-control form-control-sm"
                          placeholder="https://youtube.com/..."
                          value={video.url}
                          onChange={(e) =>
                            setIntroField(video._key, "url", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label small d-flex align-items-center gap-1">
                          <i className="bi bi-paperclip text-primary"></i>
                          Notes{" "}
                          <span className="text-secondary fw-normal ms-1">
                            (PDF, DOC, images…)
                          </span>
                        </label>
                        {video.notes.length === 0 && (
                          <p className="text-secondary small mb-1">
                            No files added yet.
                          </p>
                        )}
                        {video.notes.map((note) => (
                          <NoteFileRow
                            key={note._key}
                            note={note}
                            onTitleChange={(val) =>
                              updateIntroNote(video._key, note._key, {
                                title: val,
                              })
                            }
                            onFile={(file) =>
                              handleIntroNoteFile(video._key, note._key, file)
                            }
                            onRemove={() =>
                              removeIntroNote(video._key, note._key)
                            }
                          />
                        ))}
                        <AddBtn
                          onClick={() => addIntroNote(video._key)}
                          label="Add Note File"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className="col-12">
                <AddBtn
                  onClick={addIntroVideo}
                  label="Add Introduction Video"
                />
              </div>

              {/* ── Instructor ── */}
              <SectionHead icon="bi-person-badge" title="Instructor" />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light">
                  <div className="row g-2">
                    <div className="col-lg-2 col-md-3 col-6">
                      <label className="form-label small">ID</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="INS001"
                        value={instructor.id}
                        onChange={(e) =>
                          handleInstructorField("id", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-lg-3 col-md-5 col-12">
                      <label className="form-label small">
                        Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="John Doe"
                        value={instructor.name}
                        onChange={(e) =>
                          handleInstructorField("name", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-lg-1 col-md-2 col-4">
                      <label className="form-label small">Rating</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        placeholder="4.8"
                        min="0"
                        max="5"
                        step="0.1"
                        value={instructor.rating}
                        onChange={(e) =>
                          handleInstructorField("rating", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <label className="form-label small">
                        Bio / Description
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Senior Frontend Engineer…"
                        value={instructor.description}
                        onChange={(e) =>
                          handleInstructorField("description", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-lg-6 col-12">
                      <FileUploadField
                        label="Profile Image"
                        state={instructor.profileImage}
                        onFile={(val) =>
                          handleInstructorField("profileImage", val)
                        }
                        accept="image/*"
                        folder="courses/instructors"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Lessons ── */}
              <SectionHead icon="bi-collection-play" title="Lessons" />
              {lessons.map((lesson, lIdx) => (
                <div key={lesson._key} className="col-12">
                  <div className="border rounded-4 p-4 bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold text-secondary small">
                        Lesson {lIdx + 1}
                      </span>
                      <RemoveBtn
                        onClick={() => removeLesson(lesson._key)}
                        disabled={lessons.length === 1}
                      />
                    </div>
                    <div className="row g-2">
                      <div className="col-lg-1 col-md-2 col-4">
                        <label className="form-label small">ID</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="L001"
                          value={lesson.id}
                          onChange={(e) =>
                            setLessonField(lesson._key, "id", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-lg-5 col-md-6 col-12">
                        <label className="form-label small">
                          Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Introduction to React"
                          value={lesson.title}
                          onChange={(e) =>
                            setLessonField(lesson._key, "title", e.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="col-lg-2 col-md-3 col-6">
                        <label className="form-label small">Duration</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="15:30"
                          value={lesson.duration}
                          onChange={(e) =>
                            setLessonField(
                              lesson._key,
                              "duration",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label small">Videos</label>
                        {lesson.videos.map((vid) => (
                          <div
                            key={vid._key}
                            className="row g-2 mb-2 align-items-center"
                          >
                            <div className="col-lg-4 col-md-4 col-12">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Video Title"
                                value={vid.title}
                                onChange={(e) =>
                                  setLessonVideoField(
                                    lesson._key,
                                    vid._key,
                                    "title",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="col-lg-7 col-md-7 col-11">
                              <input
                                type="url"
                                className="form-control form-control-sm"
                                placeholder="https://youtube.com/..."
                                value={vid.url}
                                onChange={(e) =>
                                  setLessonVideoField(
                                    lesson._key,
                                    vid._key,
                                    "url",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="col-auto">
                              <RemoveBtn
                                onClick={() =>
                                  removeLessonVideo(lesson._key, vid._key)
                                }
                                disabled={lesson.videos.length === 1}
                              />
                            </div>
                          </div>
                        ))}
                        <AddBtn
                          onClick={() => addLessonVideo(lesson._key)}
                          label="Add Video"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label small d-flex align-items-center gap-1">
                          <i className="bi bi-paperclip text-primary"></i>
                          Notes{" "}
                          <span className="text-secondary fw-normal ms-1">
                            (PDF, DOC, images…)
                          </span>
                        </label>
                        {lesson.notes.length === 0 && (
                          <p className="text-secondary small mb-1">
                            No files added yet.
                          </p>
                        )}
                        {lesson.notes.map((note) => (
                          <NoteFileRow
                            key={note._key}
                            note={note}
                            onTitleChange={(val) =>
                              updateNote(lesson._key, note._key, { title: val })
                            }
                            onFile={(file) =>
                              handleNoteFile(lesson._key, note._key, file)
                            }
                            onRemove={() => removeNote(lesson._key, note._key)}
                          />
                        ))}
                        <AddBtn
                          onClick={() => addNote(lesson._key)}
                          label="Add Note File"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className="col-12">
                <AddBtn onClick={addLesson} label="Add Lesson" />
              </div>

              {/* ── Submit ── */}
              <div className="col-12 mt-4">
                <div className="row g-2">
                  {/* Status */}
                  <div className="col-lg-2 col-md-3 col-6">
                    <label className="form-label small">
                      Status <span className="text-danger fw-bold">*</span>
                    </label>
                    <select
                      name="status"
                      className="form-select form-select-sm"
                      value={form.status}
                      onChange={handleField}
                      required
                    >
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>

                  {/* Featured */}
                  <div className="col-lg-2 col-md-3 col-6">
                    <label className="form-label small d-block">Featured</label>
                    <div
                      className="d-flex align-items-center gap-2 bg-white mb-4"
                      style={{ height: 31 }}
                    >
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="featuredToggle"
                          checked={form.featured}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              featured: e.target.checked,
                            }))
                          }
                        />
                        {form.featured ? (
                          <span className="text-warning fw-semibold">
                            <i className="bi bi-star-fill me-1"></i>Featured
                          </span>
                        ) : (
                          <span className="text-secondary">Not featured</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  className={`btn px-6 fw-normal py-1 fs-5 ls-sm d-inline-flex align-items-center gap-2 ${isEdit ? "btn-warning" : "btn-success"}`}
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />{" "}
                      {isEdit ? "Updating…" : "Publishing…"}
                    </>
                  ) : (
                    <>
                      <i
                        className={`bi ${isEdit ? "bi-pencil-square" : "bi-cloud-upload"}`}
                      ></i>{" "}
                      {isEdit ? "Save Changes" : "Publish"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default AddCourse;
